import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';
import { useEffect, useRef, useState } from 'react';

import { CopilotChatSummary } from '@/state/slices/copilot/copilotChats.ts';

import styles from './Sidebar.module.css';

interface Props {
  chat: CopilotChatSummary;
  active: boolean;
  onSelect: () => void;
  onRename: (title: string) => void;
  onDelete: () => void;
}

export function ChatListItem({
  chat,
  active,
  onSelect,
  onRename,
  onDelete,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(chat.title || '');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const displayTitle =
    (chat.title && chat.title.trim()) ||
    (chat.lastMessagePreview && chat.lastMessagePreview.slice(0, 40)) ||
    'New chat';

  const commitRename = () => {
    const next = value.trim();
    setEditing(false);
    if (next && next !== chat.title) onRename(next);
  };

  return (
    <div
      className={`${styles.item} ${active ? styles.itemActive : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => !editing && onSelect()}
      onKeyDown={(e) => {
        if (!editing && (e.key === 'Enter' || e.key === ' ')) onSelect();
      }}
    >
      <div className={styles.itemBody}>
        {editing ? (
          <input
            ref={inputRef}
            className={styles.renameInput}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              // Blur so onBlur is the single commit path (avoids a double
              // commit when Enter unmounts the input).
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                setValue(chat.title || '');
                setEditing(false);
              }
              e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            <div className={styles.itemTitle}>{displayTitle}</div>
            {chat.lastMessagePreview && (
              <div className={styles.itemPreview}>
                {chat.lastMessagePreview}
              </div>
            )}
          </>
        )}
      </div>
      {!editing && (
        <div className={styles.itemActions}>
          <button
            type="button"
            aria-label="Rename"
            className={styles.itemIconBtn}
            onClick={(e) => {
              e.stopPropagation();
              setValue(chat.title || '');
              setEditing(true);
            }}
          >
            <EditOutlined />
          </button>
          <Popconfirm
            title="Delete this chat?"
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, size: 'small' }}
            cancelButtonProps={{ size: 'small' }}
            overlayClassName={styles.deletePopover}
            onConfirm={(e) => {
              e?.stopPropagation();
              onDelete();
            }}
            onCancel={(e) => e?.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Delete"
              className={styles.itemIconBtn}
              onClick={(e) => e.stopPropagation()}
            >
              <DeleteOutlined />
            </button>
          </Popconfirm>
        </div>
      )}
    </div>
  );
}
